import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface HeaderSummaryAssetKPIDataState {
  isLoading: boolean
  loadingError: string | undefined
  headerSummaryAssetKPIData: any | undefined
  httpStatus: number | undefined
}

// Initial state of the slice
const initialState: HeaderSummaryAssetKPIDataState = {
  isLoading: false,
  loadingError: undefined,
  headerSummaryAssetKPIData: undefined,
  httpStatus: undefined
}

// Slice to return from action
export const HeaderSummaryAssetKPIDataListState = createSlice({
  name: 'HeaderSummaryAssetKPIData',
  initialState,
  reducers: {
    HeaderSummaryAssetKPIDataLoadingAction: (state) => {
      // clear out the data whenever we reload
      return {
        isLoading: true,
        loadingError: undefined,
        headerSummaryAssetKPIData: undefined,
        httpStatus: 0
      }
    },
    HeaderSummaryAssetKPIDataUpdateAction: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        isLoading: false,
        loadingError: undefined,
        headerSummaryAssetKPIData: action.payload.data,
        httpStatus: action.payload.httpStatus
      }
    },
    HeaderSummaryAssetKPIDataSocketUpdateAction: (state, action: PayloadAction<any>) => {
      const updatedObj = state.headerSummaryAssetKPIData
      const { asset_id: assetId, kpi_data: kpiData } = action.payload.data
      // Update devices
      const devices = (updatedObj?.devices ?? []).map((device: any) => {
        if (device.device_id === assetId) {
          const updatedKPIReadings = [...(device.kpiData ?? [])]
          kpiData.forEach((newReading: any) => {
            const existingIndex = updatedKPIReadings.findIndex(
              (existing) => existing.kpi_definition_id === newReading.kpi_definition_id
            )
            if (existingIndex !== -1) {
              updatedKPIReadings[existingIndex] = {
                ...updatedKPIReadings[existingIndex],
                value: newReading.kpi_value,
                kpi_value: newReading.kpi_value,
                formattedValue: newReading.formattedValue,
                colorValue: newReading.colorValue,
                readingDateTime: newReading.timestamp,
                readingTime: newReading.timestamp,
                indexValue: newReading.indexValue,
                units: newReading.units,
                kpiName: newReading.kpi_name,
                kpi_definition_id: newReading.kpi_definition_id,
                execution_status: newReading.execution_status ?? updatedKPIReadings[existingIndex].execution_status ?? null
              }
            } else {
              updatedKPIReadings.push({
                value: newReading.kpi_value,
                kpi_value: newReading.kpi_value,
                formattedValue: newReading.formattedValue,
                colorValue: newReading.colorValue,
                readingDateTime: newReading.timestamp,
                readingTime: newReading.timestamp ?? new Date().toISOString(),
                indexValue: newReading.indexValue,
                units: newReading.units,
                kpiName: newReading.kpi_name,
                kpi_definition_id: newReading.kpi_definition_id,
                execution_status: newReading.execution_status ?? null
              })
            }
          })
          return {
            ...device,
            kpiData: updatedKPIReadings

          }
        }
        return device
      })

      // Update assetKPIs
      const assetKPIs = (updatedObj?.assetKPIs ?? []).map((kpi: any) => {
        const newKPI = kpiData.find((newReading: any) => newReading.kpi_definition_id === kpi.kpi_definition_id)
        if (newKPI !== undefined) {
          return {
            ...kpi,
            value: newKPI.kpi_value,
            kpi_value: newKPI.kpi_value,
            formattedValue: newKPI.formattedValue,
            colorValue: newKPI.colorValue,
            readingDateTime: newKPI.timestamp,
            readingTime: newKPI.timestamp,
            indexValue: newKPI.indexValue,
            units: newKPI.units,
            kpiName: newKPI.kpi_name,
            kpi_definition_id: newKPI.kpi_definition_id,
            execution_status: newKPI.execution_status ?? kpi.execution_status ?? null
          }
        }
        return kpi
      })

      return {
        ...state,
        headerSummaryAssetKPIData: {
          ...state.headerSummaryAssetKPIData,
          devices,
          assetKPIs
        }
      }
    },
    HeaderSummaryAssetKPIDataErrorAction: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        isLoading: false,
        loadingError: action.payload,
        httpStatus: action.payload.httpStatus,
        headerSummaryAssetKPIData: undefined
      }
    }
  }
})

// Actions to export
export const {
  HeaderSummaryAssetKPIDataLoadingAction,
  HeaderSummaryAssetKPIDataUpdateAction,
  HeaderSummaryAssetKPIDataErrorAction,
  HeaderSummaryAssetKPIDataSocketUpdateAction
} = HeaderSummaryAssetKPIDataListState.actions

export default HeaderSummaryAssetKPIDataListState.reducer
